import { useEffect } from "react";
import RoutesComponent from "Routes";
import "assets/styles/main.scss";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { ROUTE_ENUM } from "enums";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "react-tooltip/dist/react-tooltip.css";
import moment from "moment";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import "moment/locale/pl";
import "react-loading-skeleton/dist/skeleton.css";
import { HelmetProvider } from "react-helmet-async";
import { fixSierotki } from "utils/";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY || "");

export const queryClient = new QueryClient();

function ReferralListener() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");

    if (!ref) return;

    sessionStorage.setItem("referrerCode", ref);

    if (location.pathname !== ROUTE_ENUM.REGISTER) {
      navigate(ROUTE_ENUM.REGISTER, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  return null;
}

function App() {
  moment.locale("pl");

  useEffect(() => {
    const fixTextNodes = (element: Element) => {
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        null
      );

      const textNodes: Text[] = [];
      let node;
      while ((node = walker.nextNode())) {
        textNodes.push(node as Text);
      }

      textNodes.forEach((textNode) => {
        if (textNode.textContent) {
          textNode.textContent = fixSierotki(textNode.textContent);
        }
      });
    };

    const fixTextInParagraphs = (element: Element) => {
      const paragraphs = element.querySelectorAll("p");

      paragraphs.forEach((p) => {
        if (p.querySelector("a")) {
          const walker = document.createTreeWalker(
            p,
            NodeFilter.SHOW_TEXT,
            null
          );

          const textNodes: Text[] = [];
          let node;
          while ((node = walker.nextNode())) {
            textNodes.push(node as Text);
          }

          textNodes.forEach((textNode, index) => {
            if (textNode.textContent) {
              const text = textNode.textContent;
              const endsWithSierotka = /\b([aiouwzAIUOWZ])\s+$/.test(text);

              if (endsWithSierotka) {
                textNode.textContent = text.replace(
                  /\b([aiouwzAIUOWZ])\s+$/,
                  "$1\u00A0"
                );
              }

              const startsWithSpace = /^\s+/.test(text);
              if (startsWithSpace && index > 0) {
                const prevNode = textNodes[index - 1];
                if (prevNode && prevNode.textContent) {
                  const prevText = prevNode.textContent;
                  if (/\b([aiouwzAIUOWZ])$/.test(prevText)) {
                    textNode.textContent = text.replace(/^\s+/, "\u00A0");
                  }
                }
              }
            }
          });
        }
      });
    };

    const processElement = (element: Element) => {
      fixTextNodes(element);
      fixTextInParagraphs(element);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            processElement(node as Element);
          }
        });
      });
    });

    processElement(document.body);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <Elements stripe={stripePromise}>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <BrowserRouter>
              <ReferralListener />
              <RoutesComponent />
            </BrowserRouter>
          </HelmetProvider>
          <ToastContainer
            stacked
            position="bottom-right"
            autoClose={3000}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Elements>
    </div>
  );
}

export default App;
